/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
/* eslint-disable max-lines-per-function */
import { type ECharts, type SeriesModel } from 'echarts';
import { util, graphic } from 'echarts/core';
import type { ZRStyleProps } from 'echarts/types/src/util/types';
import type Element from 'zrender/lib/Element';
import type { ElementState } from 'zrender/lib/Element';
import type StandardChart from '../../core/StandardChart';
import type { ElementAnimateConfig } from '../../index';
import { isArray } from '../../util/utils';
import { BreathCircle } from './BreathPoint';
import { Line } from './VerticalPointer';

export type ModelAndView = {
  model: SeriesModel;
  view: unknown;
};

export type TimeLineHelperInitCfg = {
  // 传参 更新时间间隔
  duration?: number;
  delay?: number;
  dataIndex?: number;
  breathPoint?: {
    show?: boolean;
    shape?: {
      r?: number;
    };
    style?: ZRStyleProps;
    z: number;
  };
  verticalPointer?: {
    show?: boolean;
    style?: ZRStyleProps;
  };
  dvDuring?: (
    percent: number,
    lineClipPath: Element,
    lineModelAndView: ModelAndView,
    lineModelsAndViews: ModelAndView[]
  ) => void;
};

export type BoundingRect = { x: number; y: number; width: number; height: number };
//  ------------------------------------------------------------------------------------------------//
/**
 * 调整末尾标签动画过程中每一帧的位置，只在单个裁切矩形状态变化的回调函数中调用
 * @todo 现在是在任意一个折线的裁切矩形的状态转移动画的回调中都进行调用，性能上有很大的优化空间。
 * @param gridRect 折线对应的grid的宽高矩形
 * @param endLabel 折线对应的末尾标签的zr对象
 * @param lineModelsAndViews 图表全局series的{model，view}数组
 */
function adjustEndLablePosition(gridRect: BoundingRect, lineModelsAndViews: ModelAndView[]) {
  const { width: gridWidth, x: gridX, y: gridY, height: gridHeight } = gridRect;

  // 末尾标签到grid左右的间距
  const gridRightPadding = 6;
  const gridTopPadding = 2;
  // 末尾标签之间、末尾标签与grid上下的间隙
  const endLabelDistance = 2;
  // 末尾标签和数据点坐标的偏移 todo

  const endLabelParamsList = lineModelsAndViews
    .map((element, idx) => {
      const { view } = element;
      const { x: endLabelX, y: endLabelY } = view._endLabel as graphic.Text;

      const { width: endLabelWidth, height: endLabelHeight } = (
        view._endLabel as graphic.Text
      ).getBoundingRect();

      const adjustX =
        endLabelX + endLabelWidth > gridWidth + gridX - gridRightPadding
          ? gridWidth + gridX - gridRightPadding - endLabelWidth
          : endLabelX;
      const adjustY =
        endLabelY > gridY + gridTopPadding
          ? endLabelY + endLabelHeight < gridY + gridHeight - gridTopPadding
            ? endLabelY
            : gridY + gridHeight - gridTopPadding
          : gridY + gridTopPadding;
      return {
        x: adjustX,
        y: endLabelY,
        pointY: adjustY,
        width: endLabelWidth,
        height: endLabelHeight,
        zr: view._endLabel as Element
      };
    })
    .sort((a, b) => a.pointY - b.pointY);

  if (endLabelParamsList.length < 2) {
    endLabelParamsList.forEach(i => {
      i.zr.attr({
        x: i.x,
        y: i.pointY
      });
    });
    return;
  }

  let lastPos = gridY;

  let delta;
  const len = endLabelParamsList.length;

  // let totalShifts = 0;
  for (let i = 0; i < len; i += 1) {
    const item = endLabelParamsList[i];
    // flag之间间隔2px 目前不可配置
    const ySpace = 2;
    const { zr, pointY, height, x } = item;
    delta = pointY - lastPos;
    zr.attr('x', x);
    if (delta < 0) {
      zr.attr({
        y: pointY - delta
      });
    }
    // const shift = Math.max(-delta, 0);

    // totalShifts += shift + ySpace;

    lastPos = zr.y + height + ySpace;
  }

  const first = endLabelParamsList[0];
  const last = endLabelParamsList[len - 1];
  let minGap: number;
  let maxGap: number;
  updateMinMaxGap();

  // 如果flag区域上下两边超出给定边界，则收缩到内部占比90%（默认），然后再获取flag区域上下界
  if (minGap < 0) {
    squeezeGaps(-minGap, 1);
  }
  if (maxGap < 0) {
    squeezeGaps(maxGap, 1);
  }
  updateMinMaxGap();

  takeBoundsGap(minGap, maxGap, 1);
  takeBoundsGap(maxGap, minGap, -1);
  updateMinMaxGap();
  if (minGap < 0) {
    squeezeWhenBailout(-minGap);
  }
  if (maxGap < 0) {
    squeezeWhenBailout(maxGap);
  }
  function shiftList(dlt: number, start: number, end: number) {
    for (let i = start; i < end; i += 1) {
      const item = endLabelParamsList[i];
      const { zr } = item;
      zr.attr({
        y: zr.y + dlt
      });
    }
  }

  function updateMinMaxGap() {
    minGap = first.zr.y - gridY;
    maxGap = gridY + gridHeight - last.zr.y - last.height;
  }

  function squeezeGaps(dlt: number, maxSqueezePercent: number) {
    const gaps: number[] = [];
    let totalGaps = 0;
    for (let i = 1; i < len; i += 1) {
      const { zr, height } = endLabelParamsList[i - 1];
      const gap = Math.max(endLabelParamsList[i].zr.y - zr.y - height, 0);
      gaps.push(gap);
      totalGaps += gap;
    }
    if (!totalGaps) {
      return;
    }

    const squeezePercent = Math.min(Math.abs(dlt) / totalGaps, maxSqueezePercent);

    if (dlt > 0) {
      for (let i = 0; i < len - 1; i += 1) {
        const movement = gaps[i] * squeezePercent;
        shiftList(movement, 0, i + 1);
      }
    } else {
      for (let i = len - 1; i > 0; i -= 1) {
        const movement = gaps[i - 1] * squeezePercent;
        shiftList(-movement, i, len);
      }
    }
  }

  function takeBoundsGap(gapThisBound: number, gapOtherBound: number, moveDir: 1 | -1) {
    if (gapThisBound < 0) {
      // Move from other gap if can.
      const moveFromMaxGap = Math.min(gapOtherBound, -gapThisBound);
      if (moveFromMaxGap > 0) {
        shiftList(moveFromMaxGap * moveDir, 0, len);
        const remained = moveFromMaxGap + gapThisBound;
        if (remained < 0) {
          squeezeGaps(-remained * moveDir, 1);
        }
      } else {
        squeezeGaps(-gapThisBound * moveDir, 1);
      }
    }
  }

  function squeezeWhenBailout(dlt: number) {
    const dir = dlt < 0 ? -1 : 1;
    dlt = Math.abs(dlt);
    const moveForEachLabel = Math.ceil(dlt / (len - 1));

    for (let i = 0; i < len - 1; i += 1) {
      if (dir > 0) {
        // Forward
        shiftList(moveForEachLabel, 0, i + 1);
      } else {
        // Backward
        shiftList(-moveForEachLabel, len - i - 1, len);
      }

      dlt -= moveForEachLabel;

      if (dlt <= 0) {
        return;
      }
    }
  }

  // -----------------------------------------旧版末尾标签碰撞
  // // 从下至上解决标签之间重叠问题
  // for (let i = endLabelParamsList.length - 2; i >= 0; i--) {
  //   const item = endLabelParamsList[i];
  //   const prevItem = endLabelParamsList[i + 1];
  //   if (item.y + item.height + endLabelDistance > prevItem.y) {
  //     // 触发碰撞检测
  //     item.y = prevItem.y - endLabelDistance - item.height;
  //   }
  // }
  // // 从上至下解决标签超出问题
  // const firstItem = endLabelParamsList[0];
  // // 最上面一颗超出屏幕
  // if (firstItem.y < gridY + endLabelDistance) {
  //   // 解决第一个endLabel超出屏幕问题
  //   firstItem.y = gridY + endLabelDistance;
  //   // 遍历剩余endLabel解决影响的endLabel
  //   for (let i = 1; i < endLabelParamsList.length; i += 1) {
  //     const item = endLabelParamsList[i];
  //     const prevItem = endLabelParamsList[i - 1];
  //     if (prevItem.y + prevItem.height + endLabelDistance > item.y) {
  //       // 发生碰撞
  //       item.y = prevItem.y + prevItem.height + endLabelDistance;
  //     } else {
  //       break;
  //     }
  //   }
  // }
  // endLabelParamsList.forEach(i => {
  //   i.zr.attr({
  //     x: i.x,
  //     y: i.y
  //     // 不进行碰撞处理的y值
  //     // y: i.pointY
  //   });
  // });
}

function getClipStates(points: number[], start: number) {
  const states = {};
  for (let i = 0; i < points.length; i += 2) {
    const tmp = {
      shape: {
        width: points[i] - start
      },
      dvPointY: points[i + 1]
    };
    states[i / 2] = util.clone(tmp);
  }
  return states;
}

function clipOnDuring(
  lineClipPath: Element,
  lineModelAndView: ModelAndView,
  lineModelsAndViews: ModelAndView[],
  dvDuring?: (...args: Parameters<TimeLineHelperInitCfg['dvDuring']>) => void
) {
  const { model, view } = lineModelAndView;
  const data = model.getData();
  const endLabelModel = model.getModel('endLabel');
  const coordSys = model.coordinateSystem;
  const labelAnimationRecord = { lastFrameIndex: 0 };
  // todo lineModel依赖的axisModel类型转换为Axis2D

  // 将所有折线的modelandview按照所依赖的grid划分
  // 并且记录map和key值， 用于不同grid下的endLabel位置调整.
  // MAV: ModelAndView
  const lineMAVMap = {};
  const lineMAVKeyArr = [];
  lineModelsAndViews.forEach(item => {
    const { model: lModel } = item;
    if (!lModel.option.endLabel.show) {
      return;
    }
    const gridId = lModel.coordinateSystem.model.uid;
    if (!isArray(lineMAVMap[gridId])) {
      lineMAVKeyArr.push(gridId);
      lineMAVMap[gridId] = [];
    }
    lineMAVMap[gridId].push(item);
  });

  return (percent: number) => {
    if (endLabelModel.option.show) {
      // 实时改变endLabel的数值， 形成数值上的过渡动画
      view._endLabelOnDuring(
        percent,
        lineClipPath,
        data,
        labelAnimationRecord,
        true,
        endLabelModel,
        coordSys
      );

      // 上一状态或下一状态数据异常，则隐藏endLabel和呼吸点
      const lastState: ElementState = lineClipPath.getState(
        lineClipPath.extra.lastStateName as string
      );
      const [currentStateName] = lineClipPath.currentStates;
      const currentState: ElementState = lineClipPath.getState(currentStateName);
      if (
        !isNaN(lineClipPath.getState(lineClipPath.currentStates[0]).dvPointY) &&
        isNaN(lastState.dvPointY)
      ) {
        const points = data.getLayout('points') as number[];
        const curIndex = parseInt(currentStateName, 10);
        const curPoint = [points[curIndex * 2], points[curIndex * 2 + 1]];
        view._endLabel.attr({
          x: curPoint[0],
          y: curPoint[1]
        });
      }
      if (percent === 0) {
        view._endLabel.attr({
          ignore: isNaN(currentState.dvPointY)
        });
        view.breathCircle.attr({
          ignore: isNaN(currentState.dvPointY)
        });
      }

      const { x, y } = view._endLabel as Element;

      if (view.breathCircle) {
        // todo 呼吸点坐标需要减去endLabel的distance
        view.breathCircle.move([x - 8, y]);
        view.breathCircle.breath(true);
      }
      if (view.verticalPointer) {
        view.verticalPointer.move([x - 8, y]);
      }

      lineMAVKeyArr.forEach(key => {
        // 获取同一grid的末尾标签的grid的包围盒数据，因为都是同一个grid，所以默认取了第一个
        const gridRect = lineMAVMap[key][0].model.coordinateSystem.master.getRect();

        adjustEndLablePosition(gridRect, lineMAVMap[key]);
      });
    }
    // 自定义帧回调
    if (util.isFunction(dvDuring)) {
      dvDuring(percent, lineClipPath, lineModelAndView, lineModelsAndViews);
    }
  };
}

export default class LineTimeLineHelper {
  // standard-chart图表实例
  private _Chart: StandardChart;

  // _echart实例
  private myChart: ECharts;

  // 时间轴动画配置
  private stateTransitionConfig: ElementAnimateConfig & TimeLineHelperInitCfg;

  // 折线的model和view数组
  lineModelsAndViews: ModelAndView[];

  //
  // 传参 控制呼吸点和末尾标签和竖直光标的动画
  constructor(chart: StandardChart, transitionConfig?: TimeLineHelperInitCfg) {
    this._Chart = chart;
    this.myChart = chart.getECharts();
    this.stateTransitionConfig = util.merge(transitionConfig || {}, {
      duration: 250,
      delay: 0,
      dataIndex: 0,
      breathPoint: {
        show: true,
        shape: {
          r: 3
        },
        style: {
          fill: 'inherit'
        },
        z: 0
      },
      verticalPointer: {
        show: true,
        style: {
          color: 'inherit',
          lineDash: [2, 2]
        }
      }
    });
    this._init();
  }

  _init() {
    const { myChart, stateTransitionConfig } = this;
    this.lineModelsAndViews = myChart
      .getModel()
      .getSeriesByType('line')
      .map((model: SeriesModel) => {
        return {
          model,
          view: myChart.getViewOfSeriesModel(model)
        };
      });
    // todo 如果lineModelsAndViews为空 continue
    this.prepareClip();
    this.start(stateTransitionConfig.dataIndex);
  }

  prepareClip() {
    const { lineModelsAndViews, myChart, stateTransitionConfig } = this;
    const { breathPoint, verticalPointer } = stateTransitionConfig;
    const zr = myChart.getZr();
    lineModelsAndViews.forEach((element, index) => {
      const { view, model } = element;
      // 获取当前末尾标签是否显示
      const endLabelShow = model.getModel('endLabel').option.show;
      // 获取裁切矩形的zr实例
      const lineClipPath = view._lineGroup.getClipPath() as Element;
      // 从全局动画中删除原动画的动画器
      myChart.getZr().animation.removeAnimator(lineClipPath.animators[0]);
      // 根据配置配置呼吸点和光标
      if (view.breathCircle) {
        view.breathCircle.remove();
      }
      if (breathPoint.show) {
        const style = util.clone(breathPoint.style);
        const color =
          breathPoint.style.fill === 'inherit'
            ? model.option.itemStyle.color
            : breathPoint.style.fill;
        style.fill = color;

        if (endLabelShow) {
          view.breathCircle = new BreathCircle(zr, myChart, {
            r: breathPoint.shape.r,
            style,
            z: breathPoint.z
          });
        }
      }
      if (verticalPointer.show && !index) {
        if (!view.verticalPointer) {
          view.verticalPointer = new Line(zr, model, {
            style: verticalPointer.style
          });
        }
      }
      // 获取对应折线的全部状态，对于没有数据的点
      const points = view._points;
      const start = view._lineGroup.getClipPath().shape.x as number;
      const states = getClipStates(points, start);
      // 将不同状态对应数据的添加到clip的zr实例的状态列表中
      lineClipPath.states = util.clone(states);
      // 为每个clip实例设置状态转移的动画参数
      lineClipPath.stateTransition = util.merge(
        {
          during: clipOnDuring(
            lineClipPath,
            element,
            lineModelsAndViews,
            stateTransitionConfig.dvDuring
          ),
          force: true,
          done: () => {
            const [stateName] = lineClipPath.currentStates;
            const state = lineClipPath.getState(stateName);
            if (endLabelShow) {
              view._endLabel.attr({
                ignore: isNaN(state.dvPointY)
              });
              if (view.breathCircle) {
                view.breathCircle.attr({
                  ignore: isNaN(state.dvPointY)
                });
              }
            }
            lineClipPath.extra.lastStateName = stateName;
          },
          aborted: () => {
            const [stateName] = lineClipPath.currentStates;
            const state = lineClipPath.getState(stateName);
            if (endLabelShow) {
              view._endLabel.attr({
                ignore: isNaN(state.dvPointY)
              });
              if (view.breathCircle) {
                view.breathCircle.attr({
                  ignore: isNaN(state.dvPointY)
                });
              }
            }
            lineClipPath.extra.lastStateName = stateName;
          }
        },
        stateTransitionConfig
      );
      lineClipPath.extra = {
        lastStateName: '0'
      };
    });
  }

  start(dataIndex: number) {
    const { lineModelsAndViews } = this;
    lineModelsAndViews.forEach(element => {
      const lineClipPath = element.view._lineGroup.getClipPath() as Element;
      lineClipPath.useState(`${dataIndex}`);
      lineClipPath.stopAnimation(undefined, true);
    });
  }

  change(index: number, noAnimation = false) {
    const { lineModelsAndViews } = this;
    lineModelsAndViews.forEach((element, idx) => {
      const lineClipPath = element.view._lineGroup.getClipPath() as Element;
      const stateName = `${index}`;
      if (lineClipPath.getState(stateName)) {
        lineClipPath.useState(`${index}`, false, false);

        if (noAnimation) {
          // 保留动画的帧回调，但是不要状态转移的动画过程
          lineClipPath.stopAnimation(undefined, true);
        }
      } else {
        console.error(`没有状态${stateName}`);
      }
    });
  }

  setTransitionConfig(config: TimeLineHelperInitCfg) {
    const { stateTransitionConfig, lineModelsAndViews } = this;
    this.stateTransitionConfig = util.merge(config, stateTransitionConfig);
    lineModelsAndViews.forEach((modelAndView: ModelAndView) => {
      const { view } = modelAndView;
      // 获取裁切矩形的zr实例
      const lineClipPath = view._lineGroup.getClipPath() as Element;
      lineClipPath.stateTransition = util.merge(
        lineClipPath.stateTransition,
        this.stateTransitionConfig,
        true
      );
    });
  }
}
